<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  IconSearch,
  IconX,
  IconCirclePlus,
} from '@tabler/icons-vue'
import CollaborationMessageBubble from '@/components/CollaborationMessageBubble.vue'

type CollaborateMessage = {
  id: number
  author: string
  time: string
  text: string
  mine?: boolean
  unread?: boolean
  images?: string[]
}

const props = defineProps<{
  showSaved: boolean
}>()

const emit = defineEmits<{
  (e: 'update-unread-count', value: number): void
  (e: 'update-selected-count', value: number): void
  (e: 'saved-selection'): void
}>()

const isCollaborateDrawerOpen = ref(false)
const isCollaborateSelectMode = ref(false)
const isCollaborateSearchOpen = ref(false)
const collaborateSearchQuery = ref('')
const collaborateSearchInput = ref<HTMLInputElement | null>(null)
const collaborateTab = ref<'saved' | 'circle' | 'everyone'>('everyone')
const selectedCollaborateKeys = ref<string[]>([])
const collaborateSelectionSnapshot = ref<string[]>([])
const myCircleMessageIds = ref<number[]>([])
const collaborateDraftMessage = ref('')
const collaborateMessages = ref<CollaborateMessage[]>([
  {
    id: 1,
    author: 'Jordan A.',
    time: '5:18 PM',
    text: 'Sharing a few ceremony shots from the back row. The light when they walked down the aisle was perfect.',
    unread: true,
    images: [
      'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=320&q=60',
      'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=320&q=60',
      'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&w=320&q=60',
    ],
  },
  {
    id: 2,
    author: 'You',
    time: '5:20 PM',
    text: 'These are beautiful. I saved the aisle one for the event recap.',
    mine: true,
  },
  {
    id: 3,
    author: 'Maya Chen',
    time: '5:24 PM',
    text: 'The vows were so personal. Everyone at our table was crying by the end.',
    unread: true,
  },
  {
    id: 4,
    author: 'Avery R.',
    time: '5:27 PM',
    text: 'From near the garden arch we got a clear view of the first kiss and recessional.',
    unread: true,
  },
  {
    id: 5,
    author: 'You',
    time: '5:31 PM',
    text: 'I love how many perspectives we have from people all over the venue.',
    mine: true,
  },
  {
    id: 6,
    author: 'Caitie Buhler',
    time: '5:34 PM',
    text: 'I grabbed some reception photos too. The first dance looked amazing under the lights.',
  },
  {
    id: 7,
    author: 'Paxton Buhler',
    time: '5:39 PM',
    text: 'The toast from her brother was hilarious. I wrote down a few lines to remember later.',
    unread: true,
  },
  {
    id: 8,
    author: 'Taylor Guest',
    time: '5:42 PM',
    text: 'I uploaded a quick photo from the send-off line with everyone holding sparklers.',
    unread: true,
  },
])

const demoAvatarMap: Record<string, string> = {
  'caitie buhler': 'https://testingbot.com/free-online-tools/random-avatar/300?img=1',
  'paxton buhler': 'https://testingbot.com/free-online-tools/random-avatar/300?img=2',
}

const messageSelectionKey = (messageId: number) => `message:${messageId}`
const imageSelectionKey = (messageId: number, imageIndex: number) => `image:${messageId}:${imageIndex}`
const isSelectionKeySelected = (key: string) => selectedCollaborateKeys.value.includes(key)
const isMessageSelected = (messageId: number) =>
  isSelectionKeySelected(messageSelectionKey(messageId))
const isImageSelected = (messageId: number, imageIndex: number) =>
  isSelectionKeySelected(imageSelectionKey(messageId, imageIndex))

const unreadCollaborateCount = computed(
  () => collaborateMessages.value.filter((message) => message.unread).length,
)
const savedCollaborateMessageIds = computed(() => {
  const ids = new Set<number>()
  selectedCollaborateKeys.value.forEach((key) => {
    const [type, rawMessageId] = key.split(':')
    if (!rawMessageId) return
    if (type === 'message' || type === 'image') {
      ids.add(Number(rawMessageId))
    }
  })
  return ids
})
const tabCollaborateMessages = computed(() => {
  if (collaborateTab.value === 'saved') {
    return collaborateMessages.value.filter((message) => savedCollaborateMessageIds.value.has(message.id))
  }
  if (collaborateTab.value === 'circle') {
    return collaborateMessages.value.filter((message) => myCircleMessageIds.value.includes(message.id))
  }
  return collaborateMessages.value
})
const filteredCollaborateMessages = computed(() => {
  const query = collaborateSearchQuery.value.trim().toLowerCase()
  if (!query) return tabCollaborateMessages.value

  return tabCollaborateMessages.value.filter((message) => {
    const matchesText = message.text.toLowerCase().includes(query)
    const matchesAuthor = message.author.toLowerCase().includes(query)
    return matchesText || matchesAuthor
  })
})
const allVisibleCollaborateKeys = computed(() => {
  const keys: string[] = []
  filteredCollaborateMessages.value.forEach((message) => {
    keys.push(messageSelectionKey(message.id))
    message.images?.forEach((_, imageIndex) => {
      keys.push(imageSelectionKey(message.id, imageIndex))
    })
  })
  return keys
})
const selectedCollaborateMessages = computed(() =>
  collaborateMessages.value.filter((message) => isMessageSelected(message.id)),
)
const selectedCollaborateImages = computed(() => {
  const selectedImages: Array<{ key: string; src: string; author: string; time: string }> = []
  collaborateMessages.value.forEach((message) => {
    message.images?.forEach((src, index) => {
      const key = imageSelectionKey(message.id, index)
      if (isSelectionKeySelected(key)) {
        selectedImages.push({
          key,
          src,
          author: message.author,
          time: message.time,
        })
      }
    })
  })
  return selectedImages
})
const selectedCollaborateItemsCount = computed(
  () => selectedCollaborateMessages.value.length + selectedCollaborateImages.value.length,
)
const collaborateTabDescription = computed(() => {
  if (collaborateTab.value === 'saved') {
    return 'Saved items from My Circle and Everyone.'
  }
  if (collaborateTab.value === 'circle') {
    return 'People you know from this event.'
  }
  return 'Global attendee feed, including people you may not know.'
})
const hasSelectedCollaborateContent = computed(
  () => selectedCollaborateMessages.value.length > 0 || selectedCollaborateImages.value.length > 0,
)
const savedCollaborationThreadMessages = computed(() =>
  collaborateMessages.value.filter(
    (message) =>
      isMessageSelected(message.id) || getSavedMessageImagesWithIndex(message).length > 0,
  ),
)
const areAllVisibleCollaborateItemsSelected = computed(() => {
  if (!allVisibleCollaborateKeys.value.length) return false
  return allVisibleCollaborateKeys.value.every((key) => isSelectionKeySelected(key))
})

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

function getCollaborateAvatarImage(name: string) {
  const normalized = name.trim().toLowerCase()
  if (normalized === 'you') return null
  return demoAvatarMap[normalized] ?? null
}

function getCollaborateAvatarInitials(name: string) {
  const normalized = name.trim().toLowerCase()
  if (normalized === 'you') return 'ME'
  return getInitials(name)
}

function getCollaborateAvatarClass(name: string) {
  const normalized = name.trim().toLowerCase()
  if (normalized.includes('caitie')) return 'avatar-caitie'
  if (normalized.includes('paxton')) return 'avatar-paxton'
  if (normalized.includes('you')) return 'avatar-fallback-b'
  return 'avatar-fallback-a'
}

function selectRandomSubset(values: number[], minimum = 2) {
  if (!values.length) return []
  const shuffled = [...values]
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const randomIndex = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[randomIndex]
    shuffled[randomIndex] = temp
  }
  const subsetSize = Math.max(minimum, Math.ceil(shuffled.length * 0.55))
  return shuffled.slice(0, Math.min(subsetSize, shuffled.length))
}

function refreshMyCircleSubset() {
  const knownPeople = ['you', 'caitie buhler', 'paxton buhler', 'maya chen']
  const knownIds = collaborateMessages.value
    .filter((message) => knownPeople.includes(message.author.trim().toLowerCase()))
    .map((message) => message.id)

  const baseIds = knownIds.length ? knownIds : collaborateMessages.value.map((message) => message.id)
  myCircleMessageIds.value = selectRandomSubset(baseIds)
}

function getVisibleMessageImagesWithIndex(message: CollaborateMessage) {
  return (message.images ?? []).reduce<Array<{ src: string; imageIndex: number }>>((acc, src, imageIndex) => {
    if (collaborateTab.value !== 'saved' || isImageSelected(message.id, imageIndex)) {
      acc.push({ src, imageIndex })
    }
    return acc
  }, [])
}

function getSavedMessageImagesWithIndex(message: CollaborateMessage) {
  return (message.images ?? []).reduce<Array<{ src: string; imageIndex: number }>>((acc, src, imageIndex) => {
    if (isImageSelected(message.id, imageIndex)) {
      acc.push({ src, imageIndex })
    }
    return acc
  }, [])
}

function getSelectedImageIndexes(message: CollaborateMessage) {
  return (message.images ?? []).reduce<number[]>((acc, _, imageIndex) => {
    if (isImageSelected(message.id, imageIndex)) {
      acc.push(imageIndex)
    }
    return acc
  }, [])
}

function shouldShowMessageText(message: CollaborateMessage) {
  if (collaborateTab.value !== 'saved') return true
  return isMessageSelected(message.id)
}

function openModal() {
  refreshMyCircleSubset()
  isCollaborateDrawerOpen.value = true
  collaborateMessages.value = collaborateMessages.value.map((message) => ({
    ...message,
    unread: false,
  }))
}

function closeModal() {
  isCollaborateDrawerOpen.value = false
  isCollaborateSearchOpen.value = false
  collaborateSearchQuery.value = ''
  isCollaborateSelectMode.value = false
}

function openCollaborateSearch() {
  isCollaborateSearchOpen.value = true
  nextTick(() => {
    collaborateSearchInput.value?.focus()
  })
}

function clearCollaborateSearch() {
  collaborateSearchQuery.value = ''
  isCollaborateSearchOpen.value = false
}

function toggleCollaborateSelectionByKey(key: string) {
  if (!isCollaborateSelectMode.value) return
  if (isSelectionKeySelected(key)) {
    selectedCollaborateKeys.value = selectedCollaborateKeys.value.filter(
      (existingKey) => existingKey !== key,
    )
    return
  }
  selectedCollaborateKeys.value = [...selectedCollaborateKeys.value, key]
}

function toggleMessageSelection(messageId: number) {
  toggleCollaborateSelectionByKey(messageSelectionKey(messageId))
}

function toggleImageSelection(messageId: number, imageIndex: number) {
  toggleCollaborateSelectionByKey(imageSelectionKey(messageId, imageIndex))
}

function startCollaborateSelection() {
  collaborateSelectionSnapshot.value = [...selectedCollaborateKeys.value]
  isCollaborateSelectMode.value = true
}

function cancelCollaborateSelection() {
  selectedCollaborateKeys.value = [...collaborateSelectionSnapshot.value]
  isCollaborateSelectMode.value = false
}

function saveCollaborateSelection() {
  isCollaborateSelectMode.value = false
  emit('saved-selection')
}

function toggleSelectAllVisibleCollaborate() {
  if (areAllVisibleCollaborateItemsSelected.value) {
    selectedCollaborateKeys.value = selectedCollaborateKeys.value.filter(
      (key) => !allVisibleCollaborateKeys.value.includes(key),
    )
    return
  }

  const keySet = new Set(selectedCollaborateKeys.value)
  allVisibleCollaborateKeys.value.forEach((key) => keySet.add(key))
  selectedCollaborateKeys.value = [...keySet]
}

function getCurrentTimeLabel() {
  return new Date().toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function sendCollaborateMessage() {
  const text = collaborateDraftMessage.value.trim()
  if (!text) return

  const nextMessageId = collaborateMessages.value.length
    ? Math.max(...collaborateMessages.value.map((message) => message.id)) + 1
    : 1

  collaborateMessages.value = [
    ...collaborateMessages.value,
    {
      id: nextMessageId,
      author: 'You',
      time: getCurrentTimeLabel(),
      text,
      mine: true,
    },
  ]
  collaborateDraftMessage.value = ''
}

watch(
  unreadCollaborateCount,
  (value) => emit('update-unread-count', value),
  { immediate: true },
)

watch(
  selectedCollaborateItemsCount,
  (value) => emit('update-selected-count', value),
  { immediate: true },
)

defineExpose({
  openModal,
})
</script>

<template>
  <Teleport to="body">
    <div
      class="modal modal-bottom sm:modal-middle"
      :class="{ 'modal-open': isCollaborateDrawerOpen }"
    >
      <div class="modal-box h-[85vh] sm:h-[75vh] max-w-2xl p-0 flex flex-col overflow-hidden">
        <div class="p-3 border-b border-base-300 space-y-3">
          <div class="flex items-center justify-between">
            <div class="flex min-w-0 flex-1 items-center">
              <h3 v-if="!isCollaborateSearchOpen" class="font-semibold">Collaboration</h3>
              <div
                class="overflow-hidden transition-all duration-300 ease-out"
                :class="isCollaborateSearchOpen ? 'ml-2 w-56 sm:w-80 opacity-100' : 'w-0 opacity-0'"
              >
                <label class="input input-bordered input-sm flex items-center gap-2 w-full">
                  <IconSearch size="16" />
                  <input
                    ref="collaborateSearchInput"
                    v-model="collaborateSearchQuery"
                    type="text"
                    class="grow"
                    placeholder="Search"
                  />
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs btn-circle"
                    @click="clearCollaborateSearch"
                  >
                    <IconX size="14" />
                  </button>
                </label>
              </div>
            </div>
            <div class="flex items-center gap-2 py-2" :class="{ 'pl-2': isCollaborateSearchOpen }">
              <div v-if="!isCollaborateSelectMode">
                <button
                  v-if="!isCollaborateSearchOpen"
                  type="button"
                  class="btn btn-ghost btn-sm btn-square text-base-content/70"
                  @click="openCollaborateSearch"
                >
                  <IconSearch size="18" />
                </button>
                <button
                  class="btn btn-primary btn-sm rounded-full px-5"
                  @click="startCollaborateSelection"
                >
                  Select
                </button>
              </div>
              <div v-else class="flex items-center gap-2">
                <button
                  class="btn btn-outline btn-sm rounded-full"
                  @click="toggleSelectAllVisibleCollaborate"
                >
                  {{ areAllVisibleCollaborateItemsSelected ? 'Clear All' : 'Select All' }}
                </button>
                <button class="btn btn-ghost btn-sm rounded-full" @click="cancelCollaborateSelection">
                  <IconX size="16" />
                  Cancel
                </button>
              </div>
              <button
                type="button"
                class="btn btn-ghost btn-sm btn-square"
                @click="closeModal"
              >
                <IconX size="18" />
              </button>
            </div>
          </div>

          <div class="join w-full">
            <button
              type="button"
              class="btn join-item flex-1 btn-sm"
              :class="collaborateTab === 'saved' ? 'btn-primary' : 'btn-outline'"
              @click="collaborateTab = 'saved'"
            >
              Saved
            </button>
            <button
              type="button"
              class="btn join-item flex-1 btn-sm"
              :class="collaborateTab === 'circle' ? 'btn-primary' : 'btn-outline'"
              @click="collaborateTab = 'circle'"
            >
              My Circle
            </button>
            <button
              type="button"
              class="btn join-item flex-1 btn-sm"
              :class="collaborateTab === 'everyone' ? 'btn-primary' : 'btn-outline'"
              @click="collaborateTab = 'everyone'"
            >
              Everyone
            </button>
          </div>
          <p class="text-xs text-base-content/70 px-1">
            {{ collaborateTabDescription }}
          </p>
        </div>

        <div class="flex-1 overflow-y-auto p-3 space-y-3 bg-base-200/40">
          <CollaborationMessageBubble
            v-for="message in filteredCollaborateMessages"
            :key="message.id"
            :message="message"
            :images="getVisibleMessageImagesWithIndex(message)"
            :show-text="shouldShowMessageText(message)"
            :selectable="isCollaborateSelectMode"
            :message-selected="isMessageSelected(message.id)"
            :selected-image-indexes="getSelectedImageIndexes(message)"
            :avatar-image="getCollaborateAvatarImage(message.author)"
            :avatar-initials="getCollaborateAvatarInitials(message.author)"
            :avatar-class="getCollaborateAvatarClass(message.author)"
            @toggle-message="toggleMessageSelection"
            @toggle-image="({ messageId, imageIndex }) => toggleImageSelection(messageId, imageIndex)"
          />
        </div>

        <div
          v-if="isCollaborateSelectMode"
          class="border-t border-base-300 px-3 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] flex items-center justify-between bg-base-100"
        >
          <div class="text-sm font-semibold">
            {{ selectedCollaborateItemsCount }} selected
          </div>
          <button class="btn btn-primary rounded-full px-6 sm:btn-sm" @click="saveCollaborateSelection">
            Save
          </button>
        </div>

        <div
          v-else
          class="border-t border-base-300 px-3 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] bg-base-100"
        >
          <div class="flex w-full items-center gap-2">
            <button type="button" class="btn btn-circle btn-ghost h-12 min-h-12 w-12 sm:btn-sm sm:h-8 sm:min-h-8 sm:w-8">
              <IconCirclePlus size="22" />
            </button>
            <input
              v-model="collaborateDraftMessage"
              type="text"
              placeholder="Send message"
              class="input input-bordered grow rounded-full sm:input-sm"
              @keyup.enter="sendCollaborateMessage"
            />
            <button
              class="btn btn-ghost rounded-full px-5 sm:btn-sm"
              :disabled="!collaborateDraftMessage.trim()"
              @click="sendCollaborateMessage"
            >
              Send
            </button>
          </div>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop" @submit.prevent="closeModal">
        <button @click="closeModal">close</button>
      </form>
    </div>
  </Teleport>

  <section v-show="props.showSaved" class="px-3 pb-4 overflow-y-auto">
    <div v-if="hasSelectedCollaborateContent" class="space-y-3">
      <CollaborationMessageBubble
        v-for="message in savedCollaborationThreadMessages"
        :key="`saved-thread-${message.id}`"
        :message="message"
        :images="getSavedMessageImagesWithIndex(message)"
        :show-text="isMessageSelected(message.id)"
        :avatar-image="getCollaborateAvatarImage(message.author)"
        :avatar-initials="getCollaborateAvatarInitials(message.author)"
        :avatar-class="getCollaborateAvatarClass(message.author)"
      />
    </div>
    <div v-else class="rounded-box border border-dashed border-base-300 p-6 text-center text-sm">
      Save moments from My Circle or Everyone to pin your favorite wedding highlights here.
    </div>
  </section>
</template>

<style scoped>
.avatar-caitie {
  background: linear-gradient(145deg, #ffe7d7 0%, #f8c8a9 100%);
  color: #5b3b2a;
}

.avatar-paxton {
  background: linear-gradient(145deg, #d8e6ff 0%, #a9c7f8 100%);
  color: #1f3558;
}

.avatar-fallback-a {
  background: linear-gradient(145deg, #f3d5ff 0%, #e0b0f4 100%);
  color: #6a2963;
}

.avatar-fallback-b {
  background: linear-gradient(145deg, #d8f4ee 0%, #a9e2d3 100%);
  color: #1f5f52;
}
</style>
